import { useEffect, useRef, useState } from 'react';

type ImageOptions = {
  className?: string;
  display?: 'standard' | 'full-bleed';
  height?: Height;
  pad?: boolean;
  progressiveLoadSettings?: {
    highResQuality: number;
    lowResQuality: number;
  };
  quality?: number;
  retainRatio?: boolean;
  width?: Width;
};

type Width =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 357
  | 366
  | 380
  | 400
  | 414
  | 450
  | 474
  | 490
  | 576
  | 627
  | 672
  | 720
  | 768
  | 800
  | 900
  | 1000
  | 1024
  | 1160
  | 1440;

type Height =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 378
  | 380
  | 400
  | 414
  | 450
  | 474
  | 576
  | 653
  | 672
  | 720
  | 768
  | 800
  | 1000
  | 1024
  | 1160;

type ResponsiveImageOptions = {
  height?: Height;
  pad?: boolean;
  quality?: number;
  retainRatio?: boolean;
  src?: string;
  width?: Width;
};

export type ResponsiveImages = {
  lg?: ResponsiveImageOptions;
  md?: ResponsiveImageOptions;
  sm?: ResponsiveImageOptions;
  xl?: ResponsiveImageOptions;
  xs?: ResponsiveImageOptions;
};

type Props = {
  alt: string | undefined;
  caption?: string;
  key?: string;
  loading?: 'lazy' | 'eager';
  responsiveOptions?: ResponsiveImages;
  src: string;
} & ImageOptions;

export const smallImageOptions = ({
  pad
}: {
  pad: boolean;
}): Partial<Props> => {
  return {
    height: 300,
    pad,
    responsiveOptions: {
      lg: { height: 400, pad, width: 400 },
      sm: { height: 300, pad, width: 300 }
    },
    width: 300
  };
};

const NON_FULL_BLEED_MAX_WIDTH = 1440;
const NON_FULL_BLEED_MAX_HEIGHT = 1200;

export function addResizerFlags(
  src: string,
  { width, height, quality, pad, retainRatio, display }: ImageOptions
) {
  if (!src) return;

  const flags = [];
  const shouldCap = display !== 'full-bleed' && !width && !height;
  const effectiveWidth = shouldCap ? NON_FULL_BLEED_MAX_WIDTH : width;
  const effectiveHeight =
    shouldCap && !pad ? NON_FULL_BLEED_MAX_HEIGHT : height;

  if (effectiveWidth) flags.push(`w${effectiveWidth}`);
  if (effectiveHeight) flags.push(`h${effectiveHeight}`);
  if (quality) flags.push(`q${quality}`);
  if (pad) flags.push('pad');
  if (retainRatio || (shouldCap && !pad)) flags.push('rr');
  if (shouldCap) flags.push('ds');

  const extension = src.match(/\.[^.]+$/);

  const flagStr = `${flags.length ? '_' : ''}${flags.join('_')}_`;

  return src.replace(`${extension}`, `${flagStr}${extension}.webp`);
}

const ResponsiveSource = ({
  src,
  minWidth,
  options,
  display
}: {
  display?: ImageOptions['display'];
  minWidth: string;
  options?: ImageOptions;
  src: string;
}) => {
  if (!options) return null;

  return (
    <source
      srcSet={addResizerFlags(src, { ...options, display })}
      media={`(min-width: ${minWidth})`}
      className="h-auto max-w-full"
    />
  );
};

const Image = ({
  display,
  caption,
  className = '',
  responsiveOptions: propResponsiveOptions,
  progressiveLoadSettings,
  alt,
  loading,
  ...propOptions
}: Props) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [lowQualityLoaded, setLowQualityLoaded] = useState(false);
  const [highQualityLoaded, setHighQualityLoaded] = useState(false);

  const responsiveOptions = { ...propResponsiveOptions };
  const options = { ...propOptions };

  if (progressiveLoadSettings) {
    const currentQuality = highQualityLoaded
      ? progressiveLoadSettings.highResQuality
      : progressiveLoadSettings.lowResQuality;

    Object.keys(responsiveOptions ?? {}).forEach((k) => {
      responsiveOptions![k as keyof ResponsiveImages]!.quality = currentQuality;
    });

    options.quality = currentQuality;
  }

  useEffect(() => {
    if (!progressiveLoadSettings) return;

    if (imgRef.current?.complete && !lowQualityLoaded) {
      setLowQualityLoaded(true);
    }
  }, [lowQualityLoaded, progressiveLoadSettings]);

  useEffect(() => {
    if (!lowQualityLoaded || !progressiveLoadSettings || !imgRef.current)
      return;

    const highResSrc = imgRef.current.currentSrc.replace(
      `_q${progressiveLoadSettings?.lowResQuality}`,
      `_q${progressiveLoadSettings?.highResQuality}`
    );

    let img: HTMLImageElement | null = new window.Image();
    img.src = highResSrc;
    img.onload = () => {
      setHighQualityLoaded(true);
    };

    return () => {
      img = null;
    };
  }, [lowQualityLoaded, progressiveLoadSettings]);

  const isStandardDisplay = display === 'standard';

  return (
    <div
      className={`flex flex-col ${className} ${
        isStandardDisplay ? 'max-w-2xl' : ''
      }`}
    >
      <picture className={`block ${className}`}>
        {responsiveOptions?.xl && (
          <ResponsiveSource
            src={responsiveOptions.xl.src ?? options.src}
            minWidth="1280px"
            options={responsiveOptions?.xl}
            display={display}
          />
        )}
        {responsiveOptions?.lg && (
          <ResponsiveSource
            src={responsiveOptions.lg.src ?? options.src}
            minWidth="1024px"
            options={responsiveOptions?.lg}
            display={display}
          />
        )}
        {responsiveOptions?.md && (
          <ResponsiveSource
            src={responsiveOptions.md.src ?? options.src}
            minWidth="768px"
            options={responsiveOptions?.md}
            display={display}
          />
        )}
        {responsiveOptions?.sm && (
          <ResponsiveSource
            src={responsiveOptions.sm.src ?? options.src}
            minWidth="640px"
            options={responsiveOptions?.sm}
            display={display}
          />
        )}
        {responsiveOptions?.xs && (
          <ResponsiveSource
            src={responsiveOptions.xs.src ?? options.src}
            minWidth="200px"
            options={responsiveOptions?.xs}
            display={display}
          />
        )}
        {!responsiveOptions?.xs && (
          <ResponsiveSource
            src={options.src}
            minWidth="200px"
            options={options}
            display={display}
          />
        )}
        <img
          onLoad={() => {
            if (lowQualityLoaded || !progressiveLoadSettings) return;

            setLowQualityLoaded(true);

            // NOTE: Due to a bug this event does not fire on Firefox based browsers - https://bugzilla.mozilla.org/show_bug.cgi?id=1823852
          }}
          ref={imgRef}
          className={className}
          src={options.src}
          loading={loading}
          alt={alt}
        />
      </picture>
      {Boolean(caption) && (
        <div className="relative">
          <div className="absolute -bottom-5 right-1 text-xs font-semibold text-light-grey">
            {caption}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ImageWrapper(props: Props) {
  return <Image key={props.alt ?? props.src} {...props} />;
}
